
import { createContext, useEffect, useReducer } from 'react';

import PropTypes from 'prop-types';
// utils
import axios from '../utils/axios';
import { setSession } from '../utils/jwt';

// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  setting: null,
};
const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  SETTING: (state, action) => {
    const { setting } = action.payload;
    return {
      ...state,
      setting
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
  REGISTER: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  VERIFY: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      isAuthenticated: 'verify',
      user,
    };
  },
  CHECK: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  verify: () => Promise.resolve(),
  fetchSetting: () => Promise.resolve(),
});

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');
        if (accessToken && accessToken !== 'undefined' && accessToken !== 'null' && accessToken !== '') {
          setSession(accessToken);
          const response = await axios.get(`/api/account/my-account/${accessToken}`);
          const userData = response.data.user;

          if (response.data.status === 'success') {
            dispatch({
              type: 'INITIALIZE',
              payload: { isAuthenticated: true, user: userData },
            });
          } else if (response.data.status === 'verify') {
            dispatch({
              type: 'INITIALIZE',
              payload: { isAuthenticated: 'verify', user: userData },
            });
          } else {
            dispatch({
              type: 'INITIALIZE',
              payload: { isAuthenticated: false, user: null },
            });
          }
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: { isAuthenticated: false, user: null },
          });
        }
      } catch (err) {
        // Only clear token on a genuine 401 auth failure, not network errors
        if (err?.response?.status === 401) {
          localStorage.removeItem('accessToken');
        }
        dispatch({
          type: 'INITIALIZE',
          payload: { isAuthenticated: false, user: null },
        });
      }

      // Fetch settings separately — don't block auth initialization
      try {
        const res = await axios.get(`/api/website/app/setting`);
        dispatch({
          type: 'SETTING',
          payload: { setting: res.data },
        });
      } catch (settingsErr) {
        // Settings failure should not affect auth state
      }
    };

    initialize();
  }, []);

  const login = async (username, password) => {
    const response = await axios.post('/api/login/verify/user', { username, password });
    const accessToken = response.data.token;
    setSession(accessToken);
    const userData = response.data.user;
    if (response.data.status === 'verify') {
      dispatch({ type: 'VERIFY', payload: { user: userData } });
    } else if (response.data.status === 'success') {
      dispatch({ type: 'LOGIN', payload: { user: userData } });
    }
  };

  const register = async (email, password, name, username, phone, ref, pin) => {
    const response = await axios.post('/api/register', { email, password, name, username, phone, ref, pin });
    const accessToken = response.data.token;
    const userData = response.data.user;
    setSession(accessToken);
    if (response.data.status === 'verify') {
      dispatch({ type: 'VERIFY', payload: { user: userData } });
    } else {
      dispatch({ type: 'REGISTER', payload: { user: userData } });
    }
  };

  const verify = async (code, email) => {
    const res = await axios.post('/api/verify/user/account', { code, email });
    dispatch({ type: 'CHECK', payload: { user: res.data.user } });
  };

  const fetchSetting = async () => {
    try {
      const res = await axios.get(`/api/website/app/setting`);
      dispatch({ type: 'SETTING', payload: { setting: res.data } });
      return res.data;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    setSession(null);
    window.localStorage.clear();
    dispatch({ type: 'LOGOUT' });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'post',
        login,
        logout,
        register,
        verify,
        fetchSetting
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
